import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  MAINTENANCE_FREQUENCIES,
  MAINTENANCE_FREQUENCY_LABELS,
  formatCents,
  type MaintenanceFrequency,
  type MemberPlan,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ActiveBadge, StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Drawer } from '@/components/Modal';
import { CollapsibleSection, Field, FormSection } from '@/components/FormSection';
import { MoneyInput } from '@/components/MoneyInput';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useChartAccounts, useCostCenters } from '@/lib/queries';

/**
 * Planos do clube, vendidos por LOTE.
 *
 * O preço sobe a cada lote e cada lote tem quota. A barra mostra as vagas
 * consumidas — quando enche, a venda para sozinha. Mudar o preço aqui NÃO
 * mexe em quem já comprou: os valores são congelados na venda.
 */
export function PlanosPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MemberPlan | null>(null);
  const [removing, setRemoving] = useState<MemberPlan | null>(null);

  const listQuery = useQuery({
    queryKey: ['member-plans'],
    queryFn: () => api.get<{ data: MemberPlan[] }>('/member-plans'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['member-plans'] });
  };

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/member-plans/${id}`),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });

  const rows = listQuery.data?.data ?? [];

  const columns: Array<Column<MemberPlan>> = [
    { key: 'name', label: 'Plano', strong: true, render: (row) => row.name },
    { key: 'lotNumber', label: 'Lote', mono: true, render: (row) => row.lotNumber },
    {
      key: 'quota',
      label: 'Vagas do lote',
      render: (row) => {
        if (row.quota <= 0) return <span className="muted">sem limite</span>;
        const pct = Math.min(100, Math.round((row.soldCount / row.quota) * 100));
        const full = row.soldCount >= row.quota;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span
              aria-hidden
              style={{
                display: 'inline-block',
                width: 70,
                height: 6,
                borderRadius: 3,
                background: T.line,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: `${pct}%`,
                  background: full ? T.red : pct >= 80 ? T.amber : T.mare,
                }}
              />
            </span>
            <span className="mono">
              {row.soldCount}/{row.quota}
            </span>
            {full && <StatusBadge kind="bad">Esgotado</StatusBadge>}
          </span>
        );
      },
    },
    {
      key: 'maxPeople',
      label: 'Pessoas',
      right: true,
      mono: true,
      render: (row) => row.maxPeople,
    },
    {
      key: 'acquisitionCents',
      label: 'Adesão',
      right: true,
      mono: true,
      strong: true,
      render: (row) => formatCents(row.acquisitionCents),
    },
    {
      key: 'maintenanceCents',
      label: 'Manutenção',
      right: true,
      mono: true,
      render: (row) =>
        row.maintenanceFrequency === 'VITALICIA'
          ? '—'
          : `${formatCents(row.maintenanceCents)} · ${MAINTENANCE_FREQUENCY_LABELS[row.maintenanceFrequency].toLowerCase()}`,
    },
    { key: 'isActive', label: 'Situação', render: (row) => <ActiveBadge active={row.isActive} /> },
    {
      key: 'actions',
      label: '',
      width: 96,
      right: true,
      render: (row) => (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {can('planos-socios:editar') && (
            <Button small variant="ghost" icon={Pencil} aria-label={`Editar ${row.name}`} onClick={() => setEditing(row)} />
          )}
          {can('planos-socios:excluir') && (
            <Button small variant="ghost" icon={Trash2} aria-label={`Excluir ${row.name}`} onClick={() => setRemoving(row)} />
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Planos & Lotes"
        actions={
          can('planos-socios:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Novo plano
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {listQuery.isPending && <TableSkeleton columns={8} />}
        {listQuery.isError && (
          <ErrorState message={errorMessage(listQuery.error)} onRetry={() => void listQuery.refetch()} />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption="Planos do clube"
            columns={columns}
            rows={rows}
            emptyState={
              <EmptyState
                title="Nenhum plano cadastrado"
                description="Crie o primeiro lote do AMAI Pass para começar a vender."
                action={
                  can('planos-socios:criar') ? (
                    <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                      Novo plano
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}
      </div>

      <PlanFormDrawer
        open={creating || Boolean(editing)}
        plan={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(null);
          refresh();
        }}
      />

      <ConfirmDialog
        open={Boolean(removing)}
        destructive
        loading={removeMutation.isPending}
        title="Excluir este plano?"
        confirmLabel="Excluir"
        message={
          <>
            Só sai um plano que nunca vendeu. Se já tem associado, inative-o —
            o histórico das vendas não se apaga.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(null);
          removeMutation.reset();
        }}
        onConfirm={() => removing && removeMutation.mutate(removing.id)}
      />
    </>
  );
}

function PlanFormDrawer({
  open,
  plan,
  onClose,
  onSaved,
}: {
  open: boolean;
  plan: MemberPlan | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const costCenters = useCostCenters();
  const revenueAccounts = useChartAccounts('RECEITA');

  const [name, setName] = useState('');
  const [lotNumber, setLotNumber] = useState('1');
  const [maxPeople, setMaxPeople] = useState('4');
  const [quota, setQuota] = useState('0');
  const [acquisitionCents, setAcquisitionCents] = useState<number | null>(null);
  const [dependentCents, setDependentCents] = useState<number | null>(0);
  const [maintenanceCents, setMaintenanceCents] = useState<number | null>(0);
  const [maintenanceFrequency, setMaintenanceFrequency] = useState<MaintenanceFrequency>('ANUAL');
  const [costCenterId, setCostCenterId] = useState('');
  const [chartAccountId, setChartAccountId] = useState('');
  const [benefits, setBenefits] = useState('');
  const [contractTemplate, setContractTemplate] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? (plan?.id ?? 'new') : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setName(plan?.name ?? '');
    setLotNumber(String(plan?.lotNumber ?? 1));
    setMaxPeople(String(plan?.maxPeople ?? 4));
    setQuota(String(plan?.quota ?? 0));
    setAcquisitionCents(plan?.acquisitionCents ?? null);
    setDependentCents(plan?.dependentCents ?? 0);
    setMaintenanceCents(plan?.maintenanceCents ?? 0);
    setMaintenanceFrequency(plan?.maintenanceFrequency ?? 'ANUAL');
    setCostCenterId(plan?.costCenterId ?? '');
    setChartAccountId(plan?.chartAccountId ?? '');
    setBenefits(plan?.benefits ?? '');
    setContractTemplate(plan?.contractTemplate ?? '');
    setIsActive(plan?.isActive ?? true);
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      plan ? api.patch(`/member-plans/${plan.id}`, body) : api.post('/member-plans', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);
  const canSubmit =
    name.trim().length >= 2 && (acquisitionCents ?? 0) > 0 && costCenterId && chartAccountId;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={plan ? 'Editar plano' : 'Novo plano'}
      description="Valores são congelados na venda — editar aqui não muda quem já comprou"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                name: name.trim(),
                lotNumber: Number(lotNumber) || 1,
                maxPeople: Number(maxPeople) || 1,
                quota: Number(quota) || 0,
                acquisitionCents,
                dependentCents: dependentCents ?? 0,
                maintenanceCents: maintenanceCents ?? 0,
                maintenanceFrequency,
                costCenterId,
                chartAccountId,
                benefits: benefits.trim() || null,
                contractTemplate: contractTemplate.trim() || null,
                isActive,
              })
            }
          >
            {plan ? 'Salvar' : 'Criar'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Nome do plano" htmlFor="plan-name" error={errors.name}>
          <input
            id="plan-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="AMAI PRIME — 4 PESSOAS"
          />
        </Field>

        <div className="grid-2">
          <Field label="Lote" htmlFor="plan-lot" hint="O preço sobe a cada lote">
            <input
              id="plan-lot"
              className="input input--mono"
              inputMode="numeric"
              value={lotNumber}
              onChange={(event) => setLotNumber(event.target.value.replace(/\D/g, ''))}
            />
          </Field>
          <Field label="Vagas do lote" htmlFor="plan-quota" hint="0 = sem limite">
            <input
              id="plan-quota"
              className="input input--mono"
              inputMode="numeric"
              value={quota}
              onChange={(event) => setQuota(event.target.value.replace(/\D/g, ''))}
            />
          </Field>
        </div>

        <div className="grid-2">
          <Field label="Pessoas cobertas" htmlFor="plan-people" error={errors.maxPeople}>
            <input
              id="plan-people"
              className="input input--mono"
              inputMode="numeric"
              value={maxPeople}
              onChange={(event) => setMaxPeople(event.target.value.replace(/\D/g, ''))}
            />
          </Field>
          <Field label="Valor de adesão" htmlFor="plan-acquisition" error={errors.acquisitionCents}>
            <MoneyInput id="plan-acquisition" value={acquisitionCents} onChange={setAcquisitionCents} />
          </Field>
        </div>

        <div className="grid-2">
          <Field label="Por dependente extra" htmlFor="plan-dependent">
            <MoneyInput id="plan-dependent" value={dependentCents} onChange={setDependentCents} />
          </Field>
          <Field label="Manutenção" htmlFor="plan-maintenance">
            <MoneyInput
              id="plan-maintenance"
              value={maintenanceCents}
              onChange={setMaintenanceCents}
              disabled={maintenanceFrequency === 'VITALICIA'}
            />
          </Field>
        </div>

        <Field label="Frequência da manutenção" htmlFor="plan-frequency">
          <select
            id="plan-frequency"
            className="input"
            value={maintenanceFrequency}
            onChange={(event) => setMaintenanceFrequency(event.target.value as MaintenanceFrequency)}
          >
            {MAINTENANCE_FREQUENCIES.map((value) => (
              <option key={value} value={value}>
                {MAINTENANCE_FREQUENCY_LABELS[value]}
              </option>
            ))}
          </select>
        </Field>

        <div className="grid-2">
          <Field label="Centro de custo" htmlFor="plan-cc" error={errors.costCenterId}>
            <select
              id="plan-cc"
              className="input"
              value={costCenterId}
              onChange={(event) => setCostCenterId(event.target.value)}
            >
              <option value="">Selecione…</option>
              {(costCenters.data ?? []).map((center) => (
                <option key={center.id} value={center.id}>
                  {center.code} · {center.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Conta de receita" htmlFor="plan-account" error={errors.chartAccountId}>
            <select
              id="plan-account"
              className="input"
              value={chartAccountId}
              onChange={(event) => setChartAccountId(event.target.value)}
            >
              <option value="">Selecione…</option>
              {(revenueAccounts.data ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.code} · {account.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </FormSection>

      <CollapsibleSection label="Benefícios e contrato">
        <Field label="Benefícios (o associado lê isto)" htmlFor="plan-benefits">
          <textarea
            id="plan-benefits"
            className="input"
            rows={3}
            value={benefits}
            onChange={(event) => setBenefits(event.target.value)}
          />
        </Field>
        <Field
          label="Template do contrato"
          htmlFor="plan-contract"
          hint="Placeholders: {{nome}}, {{cpf}}, {{plano}}, {{valor}}, {{data}}"
        >
          <textarea
            id="plan-contract"
            className="input input--mono"
            rows={8}
            value={contractTemplate}
            onChange={(event) => setContractTemplate(event.target.value)}
          />
        </Field>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
          Plano ativo (à venda)
        </label>
      </CollapsibleSection>
    </Drawer>
  );
}
